export const CHECKOUT_URL = 'https://sculptors-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/hiADqzunpfb6cB4FUUXDgE/SCULPTORS-EN.png',
  creator: 'https://cdn.magicpatterns.com/uploads/aQ6vmCuwAypDvkbwQwLPkK/Ethan_Whitmore_-_RHINOPLASTY.png',
};

export const beforeItems = [
  'Opens three courses and a folder of notes before every case',
  'Starts each analysis from a blank page',
  'Observations scattered across notebooks, phones and PDFs',
  'Jumps from what they see straight to the intervention',
  'Valuable lessons fade weeks after the procedure',
];

export const afterItems = [
  'One framework to consult from first look to final review',
  'A 15-minute structure for the key elements of each case',
  'Every observation captured in consistent worksheets',
  'Clear objective hierarchy before any planning begins',
  'A growing personal library of structured case reviews',
];

export const stages = [
  { name: 'Analyze', text: 'Identify the key characteristics of the case.' },
  { name: 'Define', text: 'Clarify the primary aesthetic and functional objectives.' },
  { name: 'Plan', text: 'Organize the strategic considerations that need to be reviewed.' },
  { name: 'Prepare', text: 'Use the appropriate checklists and planning tools.' },
  { name: 'Review', text: 'Capture observations and lessons after the case.' },
];

export type Volume = {
  number: string;
  title: string;
  tagline: string;
  description: string;
  listLabel: string;
  items: string[];
  closing?: string;
};

export const volumes: Volume[] = [
  {
    number: 'Volume I',
    title: 'The Rhinoplasty Case Analysis Framework',
    tagline: 'Stop Starting Every Case From Scratch',
    description:
      'Learn how to systematically examine the key elements of a rhinoplasty case before moving into detailed planning.',
    listLabel: 'Inside',
    items: [
      'Facial analysis framework',
      'Nasal analysis worksheet',
      'Frontal-view assessment',
      'Profile assessment',
      'Basal-view assessment',
      'Symmetry review',
      'Functional considerations',
      'Patient objectives',
      'Case-analysis checklist',
      'Case summary worksheet',
    ],
    closing: 'The goal is simple: turn a complex case into an organized set of observations.',
  },
  {
    number: 'Volume II',
    title: 'The Surgical Objective Blueprint',
    tagline: 'Define What You’re Trying to Achieve Before You Plan How to Achieve It',
    description:
      'A common planning problem is jumping directly from observation to intervention. This volume helps separate: what you see → what matters → what needs to be addressed → what needs further consideration.',
    listLabel: 'Inside',
    items: [
      'Primary objective worksheet',
      'Secondary objective worksheet',
      'Aesthetic priorities',
      'Functional priorities',
      'Patient goals',
      'Case-priority matrix',
      'Objective hierarchy',
      'Planning questions',
      'Preoperative review sheet',
    ],
  },
  {
    number: 'Volume III',
    title: 'The Sculpting Strategy Workbook',
    tagline: 'Organize the Strategic Considerations Behind the Case',
    description: 'This volume transforms your observations into a structured planning document.',
    listLabel: 'Inside',
    items: [
      'Structural analysis worksheets',
      'Dorsum planning framework',
      'Tip analysis framework',
      'Middle-third considerations',
      'Lower-third considerations',
      'Soft-tissue considerations',
      'Symmetry review',
      'Structural considerations',
      'Preservation vs. modification questions',
      'Strategic planning canvas',
    ],
    closing: 'Not another lecture. A workbook designed to make your thinking visible.',
  },
  {
    number: 'Volume IV',
    title: 'The Operating Room Preparation Blueprint',
    tagline: 'Build Your Preoperative Review System Before the Procedure',
    description:
      'The purpose of this volume is to organize preparation and review — not to replace surgical judgment, institutional protocols, or formal training.',
    listLabel: 'Inside',
    items: [
      'Preoperative review checklist',
      'Case briefing sheet',
      'Surgical objective summary',
      'Equipment review checklist',
      'OR preparation worksheet',
      'Documentation checklist',
      'Team communication prompts',
      'Case-specific notes',
      'Final preoperative review',
    ],
    closing: 'Everything organized in one place.',
  },
  {
    number: 'Volume V',
    title: 'The Case Review & Learning System',
    tagline: 'Turn Every Case Into Structured Professional Learning',
    description:
      'One of the most overlooked opportunities in surgical education is what happens after the case. This volume gives you a structured way to document:',
    listLabel: 'Document',
    items: [
      'What was planned',
      'What was observed',
      'What changed',
      'What questions emerged',
      'What worked as expected',
      'What should be reviewed',
      'What should be considered differently next time',
    ],
    closing:
      'Build a personal library of lessons instead of letting valuable observations disappear after the procedure.',
  },
  {
    number: 'Volume VI',
    title: 'The Rhinoplasty Reference Library',
    tagline: 'Your Rapid-Reference System for the Entire Blueprint',
    description:
      'Instead of searching through hours of educational material, use a collection of concise reference resources organized around the Blueprint.',
    listLabel: 'Inside',
    items: [
      'Quick-reference sheets',
      'Planning matrices',
      'Analysis prompts',
      'Case-review prompts',
      'Checklists',
      'Visual frameworks',
      'Terminology reference',
      'Case-planning templates',
      'One-page summaries',
      'Mobile-friendly reference pages',
    ],
    closing: 'Designed to be consulted, not simply read once.',
  },
];

export const tools = [
  {
    icon: 'frameworks',
    title: '30+ Planning Frameworks',
    text: 'Structured worksheets and frameworks for organizing different stages of case analysis and preparation.',
  },
  {
    icon: 'checklists',
    title: 'Professional Checklists',
    text: 'Ready-to-use checklists designed to help standardize your educational planning workflow.',
  },
  {
    icon: 'mapping',
    title: 'Decision-Mapping Worksheets',
    text: 'Visual tools for organizing observations, priorities, questions, and planning considerations.',
  },
  {
    icon: 'matrices',
    title: 'Case Analysis Matrices',
    text: 'Compare observations, priorities, objectives, and areas requiring further review.',
  },
  {
    icon: 'visual',
    title: 'Visual Planning Sheets',
    text: 'Organize photographic observations and case documentation in a consistent format.',
  },
] as const;

export const bonuses = [
  {
    number: 1,
    title: '50 Rhinoplasty Case Analysis Prompts™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/bEEpWdgqGYfDMDFQENho7e/BONUS_1_SCULPTORS-EN.png',
    text: 'A collection of structured prompts designed to help you examine rhinoplasty cases from multiple angles. Use them to challenge your observations, identify unanswered questions, and develop a more systematic case-review habit.',
  },
  {
    number: 2,
    title: 'The 15-Minute Case Planning Sheet™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/8dPuwNSnFy1CjGQq7NqaM2/BONUS_2_SCULPTORS-EN.png',
    text: 'A one-page condensed version of the Sculptor’s Operating Blueprint™. Use it whenever you need a fast way to organize the key elements of a case: Analyze → Define → Plan → Prepare → Review.',
  },
  {
    number: 3,
    title: 'The Rhinoplasty Reference Cards™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/w2GZRvU9y9Y2NABuMh4g8p/BONUS_3_SCULPTORS-EN.png',
    text: 'A digital collection of quick-reference cards covering key concepts, planning questions, analysis prompts, and review reminders. Designed for fast consultation on your computer, tablet, or phone.',
  },
  {
    number: 4,
    title: 'The Case Review Master Template™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/pG7bBoKsmtMfffd8Pv38b9/BONUS_4_SCULPTORS-EN.png',
    text: 'A structured post-case worksheet designed to help you document observations, questions, changes, and learning points. Build your own growing library of case reviews over time.',
  },
  {
    number: 5,
    title: 'The Rhinoplasty Planning Checklist Pack™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/7fFdsfJK8bBF8VvPVxbD3J/BONUS_5_SCULPTORS-EN.png',
    text: 'A collection of printable checklists covering case analysis, patient objectives, preoperative review, planning, documentation, and case review — everything organized into a simple reference system.',
  },
];

export const offerItems = [
  { label: 'Sculptor’s Operating Blueprint™ — 6 Premium Volumes', value: 97 },
  { label: '50 Rhinoplasty Case Analysis Prompts™', value: 37 },
  { label: 'The 15-Minute Case Planning Sheet™', value: 29 },
  { label: 'The Rhinoplasty Reference Cards™', value: 27 },
  { label: 'The Case Review Master Template™', value: 27 },
  { label: 'The Rhinoplasty Planning Checklist Pack™', value: 27 },
];

export const testimonials = [
  {
    name: 'Dr. Elena Ramírez',
    role: 'Facial Plastic Surgeon · United States',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e80181c9-cd93-464c-8661-4db783ac713d.jpg',
    quote:
      'I used to rebuild my planning notes from scratch for every consultation. Now I open the Case Analysis Framework and my observations are organized before I even sit down to plan.',
  },
  {
    name: 'Dr. Michael Harlow',
    role: 'Otolaryngologist · United Kingdom',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/919dbcad-3dcd-4c9c-8be9-bf002a92ef27.jpg',
    quote:
      'The objective hierarchy worksheets changed how I separate functional from aesthetic priorities. It is a simple structure, but it makes my thinking far more visible.',
  },
  {
    name: 'Dr. Daniel Brooks',
    role: 'Plastic Surgeon · Canada',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d5de60c3-a0d1-498e-b28f-148a5b6e0ba1.jpg',
    quote:
      'The Case Review Master Template has become my favorite part. After a few months I have a real library of lessons instead of scattered memories of each case.',
  },
];

export const faqs = [
  {
    q: 'How will I receive the Blueprint?',
    a: 'Immediately after your purchase, you’ll receive instructions to access the digital product and all included volumes and bonuses.',
  },
  {
    q: 'Is Sculptor’s Operating Blueprint™ a surgical training course?',
    a: 'No. It is a professional educational and reference resource focused on case analysis, planning organization, documentation, and structured review. It does not replace hands-on surgical training, formal residency or fellowship education, supervision, institutional protocols, or independent clinical judgment.',
  },
  {
    q: 'Who is this product for?',
    a: 'Sculptor’s Operating Blueprint™ is designed for qualified plastic surgeons, facial plastic surgeons, otolaryngologists, and other appropriately trained medical professionals involved in rhinoplasty education and practice.',
  },
  {
    q: 'Do I need to have completed a rhinoplasty course?',
    a: 'No. However, the Blueprint is designed as a planning and reference system, not as a substitute for foundational surgical education or hands-on training.',
  },
  {
    q: 'Is the Blueprint useful if I’ve already taken rhinoplasty courses?',
    a: 'Yes. That’s one of the main reasons the Blueprint exists. Instead of replacing educational courses, it provides a structured system for organizing and reviewing information from your existing education.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'Yes. The included digital materials are provided in downloadable formats where specified.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. It is a one-time payment of $27 with lifetime access to the purchased materials, subject to the applicable purchase terms.',
  },
  {
    q: 'Will I receive updates?',
    a: 'Any future updates or additional materials will be provided according to the specific product terms communicated at the time of purchase.',
  },
  {
    q: 'What happens if I don’t like it?',
    a: 'You have a 7-day guarantee. Review the material and decide whether Sculptor’s Operating Blueprint™ is right for your professional education and reference needs. If you decide it isn’t for you, request a refund within the applicable 7-day period according to the purchase terms.',
  },
];

export const recentBuyers = [
  { name: 'Dr. Alejandro Torres', country: 'Mexico' },
  { name: 'Dra. Valentina Rojas', country: 'Colombia' },
  { name: 'Dr. Martín Fernández', country: 'Argentina' },
  { name: 'Dra. Camila Herrera', country: 'Chile' },
  { name: 'Dr. Javier Morales', country: 'Spain' },
  { name: 'Dra. Lucía Castillo', country: 'Peru' },
  { name: 'Dr. Andrés Navarro', country: 'Ecuador' },
  { name: 'Dra. Gabriela Méndez', country: 'Guatemala' },
  { name: 'Dr. Sebastián Ruiz', country: 'Uruguay' },
  { name: 'Dra. Daniela Vargas', country: 'Costa Rica' },
  { name: 'Dr. Ricardo Salazar', country: 'Venezuela' },
  { name: 'Dra. Isabel Ortega', country: 'Dominican Republic' },
  { name: 'Dr. Diego Paredes', country: 'Bolivia' },
  { name: 'Dra. Mariana Cruz', country: 'Panama' },
  { name: 'Dr. Fernando Ibarra', country: 'Paraguay' },
];
